/**
 * AskDiplomexaLiveOverlay.jsx
 * ============================
 * Floating premium "Ask Diplomexa Live" pill, mounted inside the live
 * classroom page while a delegate is joined.
 *
 * Hard rules (per product spec):
 *   • Visually a small premium gold pill at bottom-right of the wrapper.
 *   • Must NOT interfere with the 100ms iframe controls (z-index above,
 *     but `pointer-events: none` outside the pill / panel hit-zones).
 *   • Hideable / collapsible — collapsed state remembered for the session.
 *   • Shows "Premium · Uses Diploks" subtitle.
 *   • Triggers `lms_live_premium_ai_summary` flow → endpoint
 *     `POST /api/lms/live-sessions/:id/premium/in-class-notes`.
 *   • Uses the central Diploks Engine (handled server-side; we just
 *     surface low-balance / consent / refund states gracefully).
 *   • One-time consent dialog if user hasn't accepted Diploks consent.
 *   • Low-balance top-up CTA when error_code === "insufficient_balance".
 *   • Provider names + AI vendor names are NEVER mentioned anywhere.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, ChevronDown, ChevronUp, X, Loader2, Copy, Download,
  Send, CircleAlert, Wallet,
} from "lucide-react";
import { premiumInClassNotes, formatApiError } from "../../lib/liveClassApi";
import { useNavigate } from "react-router-dom";
import { DiplomexaIntelligenceOutputRenderer } from "../diplomexa-intelligence/DiplomexaIntelligenceOutput";

const LS_COLLAPSED_KEY = "diplomexa.ask_live.collapsed";

export default function AskDiplomexaLiveOverlay({
  sessionId,
  sessionTitle,
  onBalanceChange,
}) {
  // Collapse state is persistent within the tab session for nicer UX.
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem(LS_COLLAPSED_KEY) === "1"; }
    catch { return false; }
  });
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);   // { content, subtitle, ... }
  const [error, setError] = useState(null);     // structured engine error
  const navigate = useNavigate();

  // Persist collapse choice.
  useEffect(() => {
    try { sessionStorage.setItem(LS_COLLAPSED_KEY, collapsed ? "1" : "0"); }
    catch { /* noop */ }
  }, [collapsed]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const j = await premiumInClassNotes(sessionId, focus);
      setOutput(j.output);
      if (onBalanceChange && j.consume?.balance_after != null) {
        onBalanceChange(j.consume.balance_after);
      }
      toast.success(
        `Premium note ready · ${j.consume?.cost ?? 3} Diploks used`,
        { id: `ask-live-${sessionId}` },
      );
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code
                   || e?.response?.data?.error_code;
      const reason = e?.response?.data?.detail?.reason
                     || e?.response?.data?.reason
                     || formatApiError(e);
      setError({ code, reason });
      // Goodwill credit case — toast a different message so user knows.
      if (code === "feature_failed" || code === "llm_unavailable") {
        toast.message("We've added a goodwill credit to your wallet.", {
          description: "Please try again in a moment.",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const copyOutput = async () => {
    if (!output?.content) return;
    try {
      await navigator.clipboard.writeText(output.content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const downloadOutput = () => {
    if (!output?.content) return;
    const blob = new Blob([output.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `diplomexa-live-note-${sessionId}.md`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid="ask-diplomexa-live-overlay"
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 max-w-[min(420px,calc(100vw-2rem))]"
    >
      {/* Output card — only when we have a result or error to show. */}
      {(output || error) && !collapsed ? (
        <OutputCard
          output={output}
          error={error}
          onClose={() => { setOutput(null); setError(null); }}
          onCopy={copyOutput}
          onDownload={downloadOutput}
          onTopUp={() => navigate("/dashboard/diploks/topup")}
        />
      ) : null}

      {/* Pill / expanded panel */}
      {collapsed ? (
        <button
          type="button"
          data-testid="ask-diplomexa-live-pill-show"
          onClick={() => setCollapsed(false)}
          className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0CD5F] px-4 py-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#0A192F] shadow-lg shadow-black/40 hover:scale-105 transition"
        >
          <Sparkles size={14} /> Ask Diplomexa Live
        </button>
      ) : (
        <Panel
          sessionTitle={sessionTitle}
          focus={focus}
          setFocus={setFocus}
          busy={busy}
          onRun={run}
          onCollapse={() => setCollapsed(true)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Panel — expanded input form
// =============================================================================
function Panel({ sessionTitle, focus, setFocus, busy, onRun, onCollapse }) {
  return (
    <section
      data-testid="ask-diplomexa-live-panel"
      className="pointer-events-auto rounded-2xl border border-[#D4AF37]/50 bg-gradient-to-br from-[#0A192F] to-[#0F2C50] p-4 text-white shadow-2xl shadow-black/60 w-[360px]"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
            <Sparkles size={11} /> Ask Diplomexa Live
          </div>
          <p className="mt-1.5 text-[11px] text-blue-200 leading-snug">
            Live notes &amp; reflection — premium · uses Diploks (~3 / call).
          </p>
        </div>
        <button
          type="button"
          data-testid="ask-diplomexa-live-collapse"
          onClick={onCollapse}
          aria-label="Collapse Ask Diplomexa Live"
          className="rounded-full bg-white/10 hover:bg-white/20 p-1 text-white/70 hover:text-white"
        >
          <ChevronDown size={14} />
        </button>
      </header>
      <p className="mt-2 truncate text-[11px] text-blue-100/80">
        {sessionTitle ? `On: ${sessionTitle}` : null}
      </p>
      <textarea
        data-testid="ask-diplomexa-live-focus"
        value={focus}
        onChange={(e) => setFocus(e.target.value.slice(0, 400))}
        placeholder="Optional — what should the note focus on? (e.g. 'help me prep for the next caucus')"
        rows={3}
        className="mt-3 w-full resize-none rounded-lg border border-white/15 bg-black/30 px-2.5 py-2 text-[12px] text-white placeholder:text-white/40 focus:border-[#D4AF37] focus:outline-none"
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          data-testid="ask-diplomexa-live-run"
          disabled={busy}
          onClick={onRun}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#F0CD5F] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] disabled:opacity-60"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {busy ? "Composing…" : "Compose live note"}
        </button>
      </div>
      <p className="mt-2 text-[10px] text-white/50 leading-snug">
        Premium · Uses Diploks · Diplomexa Intelligence
      </p>
    </section>
  );
}

// =============================================================================
// Output / error card
// =============================================================================
function OutputCard({ output, error, onClose, onCopy, onDownload, onTopUp }) {
  return (
    <section
      data-testid="ask-diplomexa-live-output"
      className="pointer-events-auto w-[420px] max-w-full rounded-2xl border border-[#D4AF37]/50 bg-white text-[#0A192F] shadow-2xl shadow-black/60"
    >
      <header className="flex items-center justify-between gap-2 rounded-t-2xl bg-gradient-to-r from-[#0A192F] to-[#0F2C50] px-4 py-2 text-white">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
          {error ? "Live note · error" : "Live note"}
        </p>
        <button
          type="button"
          data-testid="ask-diplomexa-live-output-close"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full bg-white/10 hover:bg-white/20 p-1 text-white/70 hover:text-white"
        >
          <X size={13} />
        </button>
      </header>
      <div className="max-h-[60vh] overflow-y-auto px-4 py-3 text-[12px] leading-relaxed">
        {error ? <ErrorView error={error} onTopUp={onTopUp} /> : null}
        {output ? (
          <div data-testid="ask-diplomexa-live-content">
            <DiplomexaIntelligenceOutputRenderer
              output={output}
              title="Live Note"
              emphasis="summary"
              subtitle="Captured during your Diplomexa Live Classroom session."
              kicker="Ask Diplomexa Live"
              icon={Sparkles}
              testid="ask-diplomexa-live-renderer"
            />
          </div>
        ) : null}
      </div>
      {output ? (
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2">
          <button
            type="button"
            data-testid="ask-diplomexa-live-copy"
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 hover:bg-slate-50"
          >
            <Copy size={11} /> Copy
          </button>
          <button
            type="button"
            data-testid="ask-diplomexa-live-export"
            onClick={onDownload}
            className="inline-flex items-center gap-1 rounded-md bg-[#0F4C81] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:brightness-110"
          >
            <Download size={11} /> Export .md
          </button>
        </footer>
      ) : null}
    </section>
  );
}

function ErrorView({ error, onTopUp }) {
  const insufficient = error.code === "insufficient_balance";
  const consent = error.code === "consent_required";
  return (
    <div className="space-y-3">
      <p className="flex items-start gap-2 text-rose-700">
        <CircleAlert size={14} className="mt-0.5 shrink-0" />
        <span>{error.reason || "Something went wrong."}</span>
      </p>
      {insufficient ? (
        <button
          type="button"
          data-testid="ask-diplomexa-live-topup"
          onClick={onTopUp}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#F0CD5F] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A192F]"
        >
          <Wallet size={12} /> Top up Diploks
        </button>
      ) : null}
      {consent ? (
        <a
          href="/dashboard/diploks/consent"
          data-testid="ask-diplomexa-live-consent"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
        >
          Review Diploks consent
        </a>
      ) : null}
    </div>
  );
}
